/**
 * QorTrace · Public testimonial wall (Iter212).
 *
 * Renders the first 6 consented customer testimonials, featured-first.
 * Each card pulls the customer's uploaded logo if present; otherwise
 * falls back to a consistent monogram tile so the visual rhythm
 * stays clean.
 *
 * Name format rule (set on the backend):
 *   "Firstname L."  — first name title-cased, last name reduced to a
 *   single capitalised initial + period.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";


const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: API });


function MonogramTile({ text }) {
  return (
    <div
      aria-hidden="true"
      data-testid="testimonial-monogram"
      style={{
        width: 44, height: 44, borderRadius: 8, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(135deg, rgba(0,229,255,0.20) 0%, rgba(0,229,255,0.06) 100%)",
        border: "1px solid rgba(0,229,255,0.35)",
        color: "var(--qt-cyan)",
        fontFamily: "var(--qt-mono, monospace)",
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      {text}
    </div>
  );
}


function LogoOrMonogram({ item }) {
  const [broken, setBroken] = useState(false);
  if (!item.logo_url || broken) {
    return <MonogramTile text={item.monogram || "Q"} />;
  }
  return (
    <div
      style={{
        width: 44, height: 44, borderRadius: 8, flexShrink: 0,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid var(--qt-border)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <img
        src={item.logo_url}
        alt={`${item.company} logo`}
        width={28}
        height={28}
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ display: "block" }}
      />
    </div>
  );
}


function TestimonialCard({ item }) {
  return (
    <Link
      to={`/customer/${item.slug}/threat-radar`}
      data-testid={`testimonial-card-${item.slug}`}
      className="qt-card qt-card-interactive"
      style={{
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        padding: 24,
        height: "100%",
      }}
    >
      {/* Decorative open-quote glyph */}
      <div
        aria-hidden="true"
        className="qt-mono"
        style={{
          color: "var(--qt-cyan)",
          opacity: 0.45,
          fontSize: 40,
          lineHeight: 0.6,
          marginBottom: -8,
        }}
      >
        “
      </div>

      <p
        style={{
          color: "var(--qt-text-1, #E0E8F6)",
          fontSize: 15,
          lineHeight: 1.55,
          margin: 0,
          flex: 1,
        }}
      >
        {item.quote}
      </p>

      <div className="flex items-center gap-3">
        <LogoOrMonogram item={item} />
        <div className="min-w-0 flex-1">
          <div
            className="truncate"
            style={{ color: "#FFFFFF", fontWeight: 600, fontSize: 14 }}
          >
            {item.author}
          </div>
          <div
            className="qt-mono text-[10px] truncate"
            style={{
              color: "var(--qt-text-3)",
              letterSpacing: "0.06em",
            }}
          >
            {item.role}
            {item.company ? ` · ${item.company}` : ""}
          </div>
        </div>
        {item.featured ? (
          <span
            className="qt-mono text-[9px]"
            data-testid="testimonial-featured-pill"
            style={{
              padding: "3px 8px",
              borderRadius: 999,
              border: "1px solid #FFD479",
              color: "#FFD479",
              letterSpacing: "0.10em",
              background: "rgba(255,212,121,0.06)",
              flexShrink: 0,
            }}
          >
            FEATURED
          </span>
        ) : null}
      </div>
    </Link>
  );
}


export default function TestimonialWall() {
  const [pool, setPool] = useState([]);
  const [order, setOrder] = useState([]);            // shuffled indices into `pool`
  const [cursor, setCursor] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fade, setFade]     = useState(false);

  const VISIBLE = 3;
  const ROTATE_MS = 6000;

  // Fisher-Yates shuffle.
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    let cancelled = false;
    client.get("/api/public/customer-testimonials?limit=20")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        setPool(items);
        setOrder(shuffle(items.map((_, i) => i)));
        setCursor(0);
      })
      .catch(() => { if (!cancelled) setPool([]); });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate every ROTATE_MS — but only when:
  //   • we have more items than visible (so rotation actually changes
  //     something);
  //   • the user isn't hovering the wall (paused);
  //   • the document is visible (don't burn cycles on background tabs).
  useEffect(() => {
    if (pool.length <= VISIBLE) return;
    if (paused) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const t = setInterval(() => {
      // Trigger fade-out → swap cursor after fade-out completes
      // → fade back in.
      setFade(true);
      setTimeout(() => {
        setCursor((c) => {
          const next = c + VISIBLE;
          // If we've exhausted the queue, reshuffle the order so the
          // next page is a fresh random subset.
          if (next >= order.length) {
            setOrder(shuffle(pool.map((_, i) => i)));
            return 0;
          }
          return next;
        });
        setFade(false);
      }, 280);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pool, order, paused]);

  if (!pool || pool.length === 0) return null;

  const visible = (() => {
    if (!order.length) return pool.slice(0, VISIBLE);
    const out = [];
    for (let i = 0; i < VISIBLE && i < pool.length; i++) {
      out.push(pool[order[(cursor + i) % order.length]]);
    }
    return out;
  })();

  return (
    <section
      id="testimonial-wall"
      className="max-w-[1400px] mx-auto px-6 sm:px-8 py-20"
      data-testid="testimonial-wall"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mb-10 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div
            className="qt-label"
            style={{ color: "var(--qt-cyan)", letterSpacing: "0.16em" }}
          >
            WHAT CUSTOMERS ARE SAYING
          </div>
          <h2
            className="qt-h2 mt-2"
            style={{ fontSize: "1.9rem", color: "#fff", lineHeight: 1.2, maxWidth: 720 }}
          >
            Built for the teams already shipping post-quantum.
          </h2>
          <p
            className="mt-3"
            style={{ color: "var(--qt-text-2)", maxWidth: 680, fontSize: 15, lineHeight: 1.6 }}
          >
            These are the security, engineering, and crypto leads publishing
            their own verified threat radars on QorTrace right now.
          </p>
        </div>

        {pool.length > VISIBLE ? (
          <div
            className="qt-mono text-[10px]"
            data-testid="testimonial-rotation-pill"
            style={{
              color: "var(--qt-text-3)",
              border: "1px solid var(--qt-border)",
              borderRadius: 999,
              padding: "4px 10px",
              letterSpacing: "0.10em",
              background: "rgba(255,255,255,0.02)",
            }}
            title={paused ? "Paused while you hover" : "Auto-rotating every 6 s"}
          >
            {paused ? "PAUSED · HOVER" : "AUTO-ROTATING"} ·{" "}
            {Math.min(cursor + VISIBLE, pool.length)}/{pool.length}
          </div>
        ) : null}
      </div>

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          alignItems: "stretch",
          transition: "opacity 280ms ease",
          opacity: fade ? 0 : 1,
        }}
      >
        {visible.map((it, i) => (
          <TestimonialCard key={`${it.slug}-${cursor}-${i}`} item={it} />
        ))}
      </div>
    </section>
  );
}
